import { useCallback, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "./useAuth";

export type ThemeId = "acorns" | "campfire" | "garden" | "stars" | "magical" | "ocean" | "forest" | "night";

// To gate a theme behind an acorn purchase:
//   1. Add `premium: true, appFeatureKey: 'theme.<id>'` below
//   2. Create a shop_item in /admin/stash with item_type=app_feature and
//      app_feature_key matching the one you set above
// Profile's theme picker reads `premium` + `appFeatureKey` and checks ownership
// via useOwnsFeature; locked themes render with a 🔒 + acorn cost and route
// tappers to /stash.
export const THEMES: {
  id: ThemeId;
  emoji: string;
  label: string;
  description: string;
  premium?: boolean;
  appFeatureKey?: string;
}[] = [
  { id: "acorns",   emoji: "🐿️", label: "Mori's Acorns", description: "Squirrels & acorns" },
  { id: "campfire", emoji: "🔥", label: "Campfire",       description: "Warm sparks" },
  { id: "forest",   emoji: "🌲", label: "Forest",         description: "Trees & mushrooms" },
  { id: "night",    emoji: "🌙", label: "Night",          description: "Moon & stars" },
  { id: "garden",   emoji: "🌸", label: "Garden",         description: "Flowers & butterflies" },
  { id: "magical",  emoji: "🦄", label: "Magical",        description: "Unicorns & rainbows" },
  { id: "ocean",    emoji: "🌊", label: "Ocean",          description: "Waves & shells" },
  { id: "stars",    emoji: "⭐", label: "Starry Night",   description: "Constellations" },
];

const DEFAULT_THEME: ThemeId = "acorns";

function applyTheme(t: ThemeId) {
  document.documentElement.setAttribute("data-theme", t);
}

async function fetchTheme(userId: string): Promise<ThemeId> {
  const { data, error } = await supabase
    .from("profiles")
    .select("theme")
    .eq("id", userId)
    .single();
  if (error) throw error;
  return ((data?.theme as ThemeId | null) ?? DEFAULT_THEME);
}

/**
 * Reads the active theme from profiles, then subscribes to real-time profile
 * changes so theme updates apply instantly without a page reload (and propagate
 * to other open tabs within a couple of seconds).
 */
export function useTheme() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const userId = user?.id;

  const query = useQuery({
    queryKey: ["theme", userId],
    enabled: !!userId,
    queryFn: () => fetchTheme(userId!),
  });

  // Apply theme to the DOM whenever the cached value changes — this covers
  // the initial fetch, realtime updates, and cross-tab cache-restore.
  useEffect(() => {
    applyTheme(query.data ?? DEFAULT_THEME);
  }, [query.data]);

  // Realtime — write the new theme straight into the cache so the apply
  // effect picks it up immediately, no extra fetch needed.
  useEffect(() => {
    if (!userId) return;

    const channel = supabase
      .channel(`profile-theme:${userId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "profiles",
          filter: `id=eq.${userId}`,
        },
        (payload) => {
          const t = ((payload.new as { theme?: string }).theme as ThemeId | null) ?? DEFAULT_THEME;
          qc.setQueryData<ThemeId>(["theme", userId], t);
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [qc, userId]);

  // Optimistic local update — applies the theme immediately and seeds the
  // cache. The realtime echo from the profile UPDATE will reconcile this.
  const setTheme = useCallback(
    (t: ThemeId) => {
      qc.setQueryData<ThemeId>(["theme", userId], t);
    },
    [qc, userId],
  );

  return {
    data: query.data,
    theme: query.data ?? DEFAULT_THEME,
    isLoading: query.isLoading,
    isError: query.isError,
    error: (query.error as Error | null) ?? null,
    refetch: query.refetch,
    setTheme,
  };
}
